import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  COLUMN_MAPPING_STATUS,
  COLUMN_MAPPING_STATUS_ACTION,
} from "../../constants/columnMappingStatus";
import RowValuesPercentageLabel from "./rowValuesPercentageLabel";
import MatchingLabel from "./matchingLabel";
import RowValuesValidationLabel from "./rowValuesValidationLabel";
import { fileUploadActions } from "../../redux/reducers/fileUploadReducer";
import { FileHandler } from "../../screens/fileUploadWizard";
// core imports
import SFCard, { SFCardBody } from "../../../components/card";
import SFButton from "../../../components/button";
import { SFGridContainer, SFGridItem } from "../../../components/grid";
import { SFCheckCircleIcon, SFEditIcon } from "../../../components/icons";
import getStyled from "../../../hooks/getStyled";

const { styled } = getStyled();

const StyledActionsWrapper = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(3),
}));

const StyledTextBtn = styled(SFButton)(({ theme }) => ({
  color: theme.palette.grey500.main,
  "&:hover": {
    borderColor: theme.palette.grey500.main,
    borderRadius: 0,
  },
  "& .text": {
    marginLeft: theme.spacing(0.5),
  },
}));

const StyledCardBody = styled(SFCardBody, { shouldForwardProp: false })(
  ({ theme, action }) => ({
    height: "100%",
    display: action === COLUMN_MAPPING_STATUS_ACTION.EDIT ? "block" : "flex",
    alignItems: "center",
  })
);

const ColumnMatchResults = ({ mapping }) => {
  const reduxState = useSelector((state) => state.fileUpload);
  const [action, setAction] = useState(mapping.action);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const changeActionHandler = (action) => {
    setAction(action);
    changeMappingHandler(action, mapping.dest);
  };

  const changeMappingHandler = (action, dest) => {
    dispatch(
      fileUploadActions.setColumnAction({
        action,
        dest,
        fileData: FileHandler.fileData,
      })
    );
  };

  useEffect(() => {
    //check for required columns that do not have any missing column values and no errors
    if (mapping.src !== null){
      if (
        mapping.dest.toLocaleLowerCase() === mapping.src.toLocaleLowerCase() &&
        mapping.required &&
        //percentage is 0 when there are no missing column values
        reduxState.validationStatistics[mapping.src].percentage === 0 &&
        //errors is 0 when there are no errors
        reduxState.validationStatistics[mapping.src].errors === 0
      ) {
        changeActionHandler(COLUMN_MAPPING_STATUS_ACTION.CONFIRMED);
      }
      //check for non-required columns that do not have any missing column values and no errors
      else if (
        mapping.dest.toLocaleLowerCase() === mapping.src.toLocaleLowerCase() &&
        !mapping.required &&
        reduxState.validationStatistics[mapping.src].percentage !== 100 &&
        reduxState.validationStatistics[mapping.src].errors === 0
      ) {
        changeActionHandler(COLUMN_MAPPING_STATUS_ACTION.CONFIRMED);
      }
      //check for non-required columns that have all their column values empty
      else if (
        !mapping.required &&
        //percentage is 100 when all column values are empty
        reduxState.validationStatistics[mapping.src].percentage === 100
      ) {
        changeActionHandler(COLUMN_MAPPING_STATUS_ACTION.IGNORED);
      }
    }
  }, []);


  return (
    <SFCard>
      <StyledCardBody action={action}>
        {action === COLUMN_MAPPING_STATUS_ACTION.EDIT && (
          <>
            <SFGridContainer spacing={2}>
              <SFGridItem xs={12}>
                <MatchingLabel
                  status={mapping.status}
                  srcColName={mapping.src}
                  destColName={mapping.dest}
                />
              </SFGridItem>
              {mapping.status !== COLUMN_MAPPING_STATUS.NO_MATCH && (
                <>
                  <SFGridItem xs={12}>
                    <RowValuesPercentageLabel
                      percentage={
                        mapping.src
                          ? reduxState.validationStatistics[mapping.src]
                            .percentage
                          : 0
                      }
                    />
                  </SFGridItem>
                  <SFGridItem xs={12}>
                    <RowValuesValidationLabel
                      percentage={
                        mapping.src
                          ? reduxState.validationStatistics[mapping.src].errors
                          : 0
                      }
                    />
                  </SFGridItem>
                </>
              )}
            </SFGridContainer>
            <StyledActionsWrapper>
              <SFGridContainer alignItems="center">
                <SFGridItem xs="auto">
                  <SFButton
                    disabled={mapping.status === COLUMN_MAPPING_STATUS.NO_MATCH}
                    variant="secondary"
                    onClick={() =>
                      changeActionHandler(
                        COLUMN_MAPPING_STATUS_ACTION.CONFIRMED
                      )
                    }
                  >
                    {t("fileUploadWizard.fileMapping.confirmMappingBtn")}
                  </SFButton>
                </SFGridItem>
                {!mapping.required && (
                  <SFGridItem xs="auto">
                    <StyledTextBtn
                      variant="text"
                      onClick={() =>
                        changeActionHandler(
                          COLUMN_MAPPING_STATUS_ACTION.IGNORED
                        )
                      }
                    >
                      {t("fileUploadWizard.fileMapping.ignoreColumnBtn")}
                    </StyledTextBtn>
                  </SFGridItem>
                )}
              </SFGridContainer>
            </StyledActionsWrapper>
          </>
        )}
        {action !== COLUMN_MAPPING_STATUS_ACTION.EDIT && (
          <SFGridContainer justifyContent="space-between" alignItems="center">
            <SFGridItem xs="auto">
              <SFGridContainer alignItems="center">
                <SFGridItem xs={2}>
                  <div className="d-flex">
                    <SFCheckCircleIcon
                      size="small"
                      color={
                        action === COLUMN_MAPPING_STATUS_ACTION.CONFIRMED
                          ? "green300"
                          : "grey300"
                      }
                    />
                  </div>
                </SFGridItem>
                <SFGridItem xs={10}>
                  {action === COLUMN_MAPPING_STATUS_ACTION.CONFIRMED
                    ? t("fileUploadWizard.fileMapping.confirmedMappingLabel")
                    : t("fileUploadWizard.fileMapping.ignoredColumnLabel")}
                </SFGridItem>
              </SFGridContainer>
            </SFGridItem>
            <SFGridItem xs="auto">
              <StyledTextBtn
                size="small"
                variant="text"
                onClick={() =>
                  changeActionHandler(COLUMN_MAPPING_STATUS_ACTION.EDIT)
                }
              >
                <SFEditIcon size="small" />
                <span className="text">
                  {t("fileUploadWizard.fileMapping.editBtn")}
                </span>
              </StyledTextBtn>
            </SFGridItem>
          </SFGridContainer>
        )}
      </StyledCardBody>
    </SFCard>
  );
};

ColumnMatchResults.propTypes = {
  mapping: PropTypes.object.isRequired,
};

export default ColumnMatchResults;
